import { useRouter } from "next/navigation";
import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "../../shadcn/dialog";
import { SearchBar } from "../../SearchBar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn/select";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import {
  Building2,
  LayoutGrid,
  Users2,
  MessageSquare,
  Wrench,
  Ticket,
  Database,
  Plug,
  Plus,
  SearchX,
  ChevronRight,
  Search,
  Vault,
} from "lucide-react";
import { SpotToast } from "../../SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import {
  POPULAR_TYPES,
  getAdminOAuthSetupRequiredDetails,
  hasOAuthAppForm,
  startOAuthFlow,
  type AdminOAuthSetupRequiredDetails,
} from "./utils";
import { IntegrationCard } from "./IntegrationCard";
import { IntegrationConnectForm } from "./IntegrationConnectForm";
import { CustomMCPConnectForm } from "./CustomMCPConnectForm";
import { AdminOAuthSetupRequiredDialog } from "@/components/MCP/AdminOAuthSetupRequiredDialog";
import type { IntegrationInfo } from "./types";
import { cn } from "@/lib/utils";

type ConnectTarget =
  | { kind: "integration"; integration: IntegrationInfo }
  | { kind: "customMcp" }
  | null;

export function AddIntegrationModal({
  open,
  onClose,
  integrations,
  groupId,
  returnTo,
  onConnected,
}: {
  open: boolean;
  onClose: () => void;
  integrations: IntegrationInfo[];
  groupId: string;
  returnTo: string;
  // Fires after a successful connection so the parent (LinkedAccounts)
  // can invalidate its queries — the modal closes via ``onClose`` and
  // ``onConnected`` runs in the same tick before the close handler.
  onConnected?: () => void;
}) {
  const router = useRouter();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [connectTarget, setConnectTarget] = useState<ConnectTarget>(null);
  const [adminSetupRequired, setAdminSetupRequired] =
    useState<AdminOAuthSetupRequiredDetails | null>(null);

  const CATEGORY_ICONS: Record<string, React.ElementType> = {
    CRM: Users2,
    Productivity: LayoutGrid,
    Communication: MessageSquare,
    Service: Wrench,
    Ticketing: Ticket,
    Data: Database,
    MCP: Plug,
    Vault: Vault,
  };

  const platformCategory = "All";
  const mcpCategory = "MCP";
  const standardCategories = [
    "CRM",
    "Productivity",
    "Communication",
    "Service",
    "Ticketing",
    "Data",
    "Vault",
  ];

  const customMcpAvailable = integrations.some(
    (i) => i.type === "mcp_custom" && i.enabled !== false
  );
  const anyMcpAvailable = integrations.some(
    (i) => i.category === mcpCategory && i.enabled !== false
  );

  const [authFilter, setAuthFilter] = useState<"All" | "Secret" | "OAuth">(
    "All"
  );

  // Reset all filters whenever the modal opens so stale state never leaks across sessions
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedCategory("All");
      setAuthFilter("All");
      setConnectTarget(null);
    }
  }, [open]);

  // If a background refetch removes the MCP category while the modal is
  // open (admin disabled the last MCP in another tab), fall back to the
  // Platform tab so the content area doesn't show an orphan empty state
  // with no matching sidebar tab.
  useEffect(() => {
    if (!anyMcpAvailable && selectedCategory === mcpCategory) {
      setSelectedCategory(platformCategory);
    }
  }, [anyMcpAvailable, selectedCategory, mcpCategory, platformCategory]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return integrations
      .filter((i) => i.visible !== false)
      .filter((i) => i.enabled !== false)
      .filter((i) => i.display_name.toLowerCase().includes(q))
      .filter((i) => {
        if (selectedCategory === platformCategory)
          return i.category !== mcpCategory;
        if (selectedCategory === mcpCategory) return i.category === mcpCategory;
        const cat = i.category || "Other";
        if (cat === selectedCategory) return true;
        return (
          i.category === mcpCategory && i.mcp_category === selectedCategory
        );
      })
      .filter((i) => i.providers.some((p) => p.available))
      .filter((i) => {
        if (authFilter === "All") return true;
        if (authFilter === "OAuth")
          return i.providers.some(
            (p) => p.auth_type !== "secret_auth" && p.available
          );
        return i.providers.some(
          (p) => p.auth_type === "secret_auth" && p.available
        );
      });
  }, [integrations, search, selectedCategory, authFilter]);

  const { popular, rest } = useMemo(() => {
    const pop = filtered
      .filter((i) => POPULAR_TYPES.has(i.type))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    const other = filtered
      .filter((i) => !POPULAR_TYPES.has(i.type))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    return { popular: pop, rest: other };
  }, [filtered]);

  const handleCardClick = (integration: IntegrationInfo) => {
    const availableProviders = integration.providers.filter((p) => p.available);
    if (availableProviders.length === 0) return;

    analytics_track(ANALYTICS_EVENTS.INTEGRATIONS_CONNECT_ACCOUNT, {
      integration_name: integration.type,
    });

    // Single OAuth-only provider -> start OAuth redirect directly (no dialog to replace).
    if (
      availableProviders.length === 1 &&
      availableProviders[0].auth_type !== "secret_auth" &&
      !hasOAuthAppForm(availableProviders[0])
    ) {
      startOAuthFlow(groupId, integration, availableProviders[0], returnTo)
        .then((url) => router.push(url))
        .catch((err) => {
          const adminDetails = getAdminOAuthSetupRequiredDetails(err);
          if (adminDetails) {
            setAdminSetupRequired(adminDetails);
            return;
          }
          SpotToast.error(
            "Failed to start OAuth connection. Please try again."
          );
        });
      return;
    }

    setConnectTarget({ kind: "integration", integration });
  };

  const inConnectView = connectTarget !== null;
  const breadcrumbLabel =
    connectTarget?.kind === "integration"
      ? connectTarget.integration.display_name
      : connectTarget?.kind === "customMcp"
        ? "Add custom MCP"
        : "";

  return (
    <>
      <AdminOAuthSetupRequiredDialog
        open={adminSetupRequired !== null}
        onClose={() => setAdminSetupRequired(null)}
        details={adminSetupRequired}
      />
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-[900px] p-0 gap-0 overflow-hidden">
          <div className="flex h-[580px] overflow-hidden">
            {/* Sidebar */}
            <div
              className={cn(
                "border-r border-border flex flex-col shrink-0 bg-muted/30 overflow-hidden transition-all duration-300 ease-in-out",
                inConnectView ? "w-0 border-r-0" : "w-[220px]"
              )}
            >
              <div className="px-5 pt-5 pb-4">
                <DialogTitle className="text-md font-semibold">
                  Add connection
                </DialogTitle>
              </div>
              <nav className="flex flex-col gap-1 px-3 flex-1">
                {(() => {
                  const isActive = selectedCategory === platformCategory;
                  return (
                    <button
                      key={platformCategory}
                      onClick={() => setSelectedCategory(platformCategory)}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer",
                        isActive
                          ? "bg-secondary text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-normal"
                      )}
                    >
                      <Building2 size={16} className="shrink-0" />
                      Platform
                    </button>
                  );
                })()}
                {anyMcpAvailable &&
                  (() => {
                    const McpIcon = CATEGORY_ICONS[mcpCategory];
                    const isActive = selectedCategory === mcpCategory;
                    return (
                      <button
                        key={mcpCategory}
                        onClick={() => setSelectedCategory(mcpCategory)}
                        className={cn(
                          "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer",
                          isActive
                            ? "bg-secondary text-foreground font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-normal"
                        )}
                      >
                        {McpIcon && <McpIcon size={16} className="shrink-0" />}
                        {mcpCategory}
                      </button>
                    );
                  })()}
                <div className="my-2 h-px bg-border mx-1" />
                {standardCategories.map((cat) => {
                  const CatIcon = CATEGORY_ICONS[cat];
                  const isActive = selectedCategory === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer",
                        isActive
                          ? "bg-secondary text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-normal"
                      )}
                    >
                      {CatIcon && <CatIcon size={16} className="shrink-0" />}
                      {cat}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main content */}
            <div className="flex flex-col flex-1 overflow-hidden">
              {inConnectView ? (
                <>
                  <div className="pl-6 pr-5 pt-5 pb-3 shrink-0">
                    <div className="flex items-center gap-1.5 text-sm">
                      <button
                        onClick={() => setConnectTarget(null)}
                        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 hover:cursor-pointer"
                      >
                        Add connection
                      </button>
                      <ChevronRight
                        size={12}
                        className="text-muted-foreground"
                      />
                      <span className="flex items-center gap-1.5 text-foreground font-medium">
                        {breadcrumbLabel}
                      </span>
                    </div>
                  </div>

                  {connectTarget?.kind === "integration" && (
                    <IntegrationConnectForm
                      integration={connectTarget.integration}
                      groupId={groupId}
                      returnTo={returnTo}
                      onBack={() => setConnectTarget(null)}
                      onSuccess={() => {
                        onConnected?.();
                        onClose();
                      }}
                      onAdminSetupRequired={setAdminSetupRequired}
                    />
                  )}

                  {connectTarget?.kind === "customMcp" && (
                    <CustomMCPConnectForm
                      groupId={groupId}
                      returnTo={returnTo}
                      onBack={() => setConnectTarget(null)}
                    />
                  )}
                </>
              ) : (
                <div className="flex flex-col flex-1 overflow-hidden gap-4">
                  <h1 className="text-md font-medium text-primary px-5 pt-5">
                    Integrations
                  </h1>
                  {/* Search + filter bar */}
                  <div className="px-5 shrink-0 flex items-center gap-2 mb-2">
                    <SearchBar
                      placeholder="Search integrations"
                      value={search}
                      setValue={(value: string) => setSearch(value)}
                    />
                    <Select
                      value={authFilter}
                      onValueChange={(v) =>
                        setAuthFilter(v as "All" | "Secret" | "OAuth")
                      }
                    >
                      <SelectTrigger className="w-[130px] shrink-0 ring-0 focus:ring-0">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All types</SelectItem>
                        <SelectItem value="Secret">Secret</SelectItem>
                        <SelectItem value="OAuth">OAuth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Integration grid */}
                  <div
                    className="flex flex-col gap-6 overflow-y-auto flex-1 px-5 pr-1 -mr-1 pb-5"
                    style={{ scrollbarGutter: "stable" }}
                  >
                    {selectedCategory === mcpCategory &&
                      !search &&
                      customMcpAvailable && (
                        <button
                          type="button"
                          onClick={() =>
                            setConnectTarget({ kind: "customMcp" })
                          }
                          className="flex items-center gap-3 p-3 pr-4 w-full rounded-lg border border-border bg-card/50 shadow-xs cursor-pointer hover:border-ring hover:bg-card text-left"
                        >
                          <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border/40 flex items-center justify-center">
                            <Plus size={18} className="text-muted-foreground" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-sm text-foreground truncate">
                              Add custom MCP
                            </span>
                            <span className="text-xs text-muted-foreground truncate">
                              Connect any remote MCP server by URL
                            </span>
                          </div>
                        </button>
                      )}
                    {selectedCategory !== "All" && filtered.length > 0 ? (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-3">
                          {selectedCategory}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {filtered
                            .sort((a, b) =>
                              a.display_name.localeCompare(b.display_name)
                            )
                            .map((i) => (
                              <IntegrationCard
                                key={i.type}
                                integration={i}
                                onClick={handleCardClick}
                                connecting={null}
                              />
                            ))}
                        </div>
                      </div>
                    ) : search && filtered.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {filtered
                          .sort((a, b) =>
                            a.display_name.localeCompare(b.display_name)
                          )
                          .map((i) => (
                            <IntegrationCard
                              key={i.type}
                              integration={i}
                              onClick={handleCardClick}
                              connecting={null}
                            />
                          ))}
                      </div>
                    ) : (
                      <>
                        {popular.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3">
                              Popular
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {popular.map((i) => (
                                <IntegrationCard
                                  key={i.type}
                                  integration={i}
                                  onClick={handleCardClick}
                                  connecting={null}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {rest.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground mb-3">
                              All Integrations
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {rest.map((i) => (
                                <IntegrationCard
                                  key={i.type}
                                  integration={i}
                                  onClick={handleCardClick}
                                  connecting={null}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {filtered.length === 0 && (
                      <div className="mb-6 h-full">
                        <NoxusEmptyBlock
                          title="No integrations"
                          description={
                            search ? (
                              <>
                                No integrations found for &ldquo;{search}
                                &rdquo;.
                              </>
                            ) : (
                              "No integrations found for the applied filters."
                            )
                          }
                          icon={SearchX}
                          size="sm"
                          button="Clear filters"
                          buttonVariant="outline"
                          onClick={() => {
                            setSearch("");
                            setAuthFilter("All");
                            setSelectedCategory(platformCategory);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
